import { useEffect, useState } from "react";
import SubtitleAndDescription from "components/Common/SubtitleAndDescription";
import { Item } from "./BuildPageContainer";
import { ReactComponent as SoulsIcon } from "svg/deadlock-souls.svg";
import classNames from "classnames";
import { TooltipContainer } from "@outplayed/tooltips";
import { getDeadlockImagePath } from "lib/general-helper";
import ItemTooltip from "components/ItemTooltip";
import { ReactComponent as TooltipIcon } from "svg/question-line.svg";

interface AbilitiesData {
  [abilityKey: string]: {
    id: number;
    name: string;
    abilityImage: string;
  };
}

interface ItemsForStageProps {
  index: number;
  item: Item;
  abilitiesData: AbilitiesData | null;
}

const ItemsForStage = (props: ItemsForStageProps) => {
  const { index, item, abilitiesData } = props;
  const iconImage = getDeadlockImagePath(item.iconPath ?? "");

  const imbuedID = item?.imbuedID;

  // Attempt to find a matching ability name based on imbuedID
  let abilityName: string | null = null;
  let abilityIcon;
  if (imbuedID && abilitiesData) {
    const matchingAbilityKey = Object.keys(abilitiesData).find((key) => abilitiesData[key].id === imbuedID);
    if (matchingAbilityKey) {
      abilityName = abilitiesData[matchingAbilityKey].name;
      abilityIcon = getDeadlockImagePath(abilitiesData[matchingAbilityKey].abilityImage, abilitiesData[matchingAbilityKey].name);
    }
  }

  return (
    <div
      className={classNames("item-container", {
        "has-imbue": !!imbuedID,
      })}
    >
      <div className="index-number">{index + 1}</div>
      <TooltipContainer tooltipInfo={<ItemTooltip item={item} />}>
        <div
          className={classNames("item-icon-container", {
            orange: item.type === "EItemSlotType_WeaponMod",
            green: item.type === "EItemSlotType_Armor",
            purple: item.type === "EItemSlotType_Tech",
          })}
        >
          <img src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${iconImage}.png`} alt={item.name} />
          {item.isComponent && <div className="component-indicator" />}
        </div>
      </TooltipContainer>
      {abilityName && (
        <TooltipContainer
          tooltipInfo={
            <div>
              <span className="imbue-text">
                Imbue <strong>{item.name}</strong> into <strong>{abilityName}</strong>
              </span>
            </div>
          }
        >
          <div className="ability-imbue">
            <img
              src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${abilityIcon}.png`}
              alt={abilityName}
              className="tooltip-icon"
            />
          </div>
        </TooltipContainer>
      )}
      <div className="item-name">{item.name}</div>
      <div className="cost-container">
        <SoulsIcon className="souls-icon" /> {item.cost}
      </div>
    </div>
  );
};

const OptionalItems = (props: { optionalItems: Item[] }) => {
  return (
    <div className="items-list-container">
      {props.optionalItems.map((item, index) => {
        const iconImage = getDeadlockImagePath(item.iconPath ?? "");
        return (
          <div className="item-container" key={index}>
            <TooltipContainer tooltipInfo={<ItemTooltip item={item} />}>
              <div
                className={classNames("item-icon-container", {
                  orange: item.type === "EItemSlotType_WeaponMod",
                  green: item.type === "EItemSlotType_Armor",
                  purple: item.type === "EItemSlotType_Tech",
                })}
              >
                <img src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${iconImage}.png`} alt={item.name} />
              </div>
            </TooltipContainer>
            <div className="item-name">{item.name}</div>
            <div className="cost-container">
              <SoulsIcon className="souls-icon" /> {item.cost}
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface BuildOrderProps {
  items: {
    earlyGameItems: Item[];
    midGameItems: Item[];
    lateGameItems: Item[];
  };
  optionalEarlyItems: Item[];
  optionalLateItems: Item[];
  sellItems: Item[];
}

const BuildOrder = (props: BuildOrderProps) => {
  const { items, optionalEarlyItems, optionalLateItems, sellItems } = props;

  // Store the entire Abilities JSON in state
  const [abilitiesData, setAbilitiesData] = useState<AbilitiesData | null>(null);

  // Fetch the JSON when component mounts
  useEffect(() => {
    fetch("https://static.bigbrain.gg/deadlock/staticdata/gamedata/Abilities_English.json")
      .then((res) => res.json())
      .then((data: AbilitiesData) => {
        setAbilitiesData(data);
      })
      .catch((err) => console.error("Error fetching abilities data:", err));
  }, []);

  return (
    <div className="build-order-wrapper">
      <div className="stage-wrapper">
        <SubtitleAndDescription subtitle="Early Game" />
        <div className="legend">
          <div className="legend-component-indicator" />
          <div className="legend-label">Component</div>
          <TooltipContainer
            tooltipInfo={
              <div>
                <strong>Components</strong> build into later final items. Purchasing a <strong>Component</strong> reduces the
                final item’s cost by the full <strong>Component</strong> price.
              </div>
            }
          >
            <div className="tooltip-container">
              <TooltipIcon className="tooltip-icon" />
            </div>
          </TooltipContainer>
        </div>
        <div className="items-wrapper">
          {items.earlyGameItems.map((item, index) => (
            <ItemsForStage index={index} item={item} key={index} abilitiesData={abilitiesData} />
          ))}
        </div>
        <div className="stage-name">Optional Items</div>
        <OptionalItems optionalItems={optionalEarlyItems} />
      </div>

      <div className="stage-wrapper">
        <SubtitleAndDescription subtitle="Mid Game" />
        <div className="items-wrapper">
          {items.midGameItems.map((item, index) => (
            <ItemsForStage index={index + items.earlyGameItems.length} item={item} key={index} abilitiesData={abilitiesData} />
          ))}
        </div>
        <div className="stage-name">Sell Priority</div>
        <div className="items-list-container">
          {sellItems.map((item, index) => {
            const iconImage = getDeadlockImagePath(item.iconPath ?? "");
            return (
              <div className="item-container" key={index}>
                <TooltipContainer tooltipInfo={<ItemTooltip item={item} />}>
                  <div
                    className={classNames("item-icon-container", {
                      orange: item.type === "EItemSlotType_WeaponMod",
                      green: item.type === "EItemSlotType_Armor",
                      purple: item.type === "EItemSlotType_Tech",
                    })}
                  >
                    <img src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${iconImage}.png`} alt={item.name} />
                  </div>
                </TooltipContainer>
                <div className="item-name">{item.name}</div>
                <div className="cost-container sell">
                  <SoulsIcon className="souls-icon" /> {item.cost}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="stage-wrapper">
        <SubtitleAndDescription subtitle="Late Game" />
        <div className="items-wrapper">
          {items.lateGameItems.map((item, index) => (
            <ItemsForStage
              index={index + items.earlyGameItems.length + items.midGameItems.length}
              item={item}
              key={index}
              abilitiesData={abilitiesData}
            />
          ))}
        </div>
        <div className="stage-name">Optional Items</div>
        <OptionalItems optionalItems={optionalLateItems} />
      </div>
    </div>
  );
};

export default BuildOrder;
